/**
 * Loading states for Logs, Metrics and the Safety Centre. They are shaped like the finished screens
 * (same header, same frames) so nothing jumps when the data lands. Kept in their own small module so
 * routes can show them while a screen's code is still downloading.
 */
import { useProjectSlug } from "@/app/context";
import { Page, PageHeader } from "@/app/Page";
import { Card, Loading, Skeleton } from "@/design";
import { useProject } from "@/lib/api";

/* ------------------------------------------------------------------ Logs */

export function LogsSkeleton() {
  return (
    <div className="flex-1 overflow-hidden px-4 py-2" role="status" aria-busy="true">
      <span className="sr-only">Loading logs</span>
      {Array.from({ length: 14 }, (_, i) => (
        <div key={i} className="flex items-center gap-3 py-1.5">
          <Skeleton className="h-3.5 w-20" />
          <Skeleton className="hidden h-3.5 w-10 sm:block" />
          <Skeleton className="hidden h-3.5 w-20 sm:block" />
          <div className="min-w-0 flex-1">
            <Skeleton className="h-3.5" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function LogsPending() {
  return (
    <div className="flex h-[calc(100dvh-3.5rem)] min-h-[560px] flex-col">
      <div className="px-4 pt-6 sm:px-6 lg:px-10 lg:pt-8">
        <header className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div className="min-w-0">
            <h1 className="font-display text-3xl font-semibold tracking-tight text-text">Logs</h1>
            <p className="mt-1.5 max-w-2xl text-md text-muted">Everything your services print, plus every request they answer, in one place.</p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <Skeleton className="h-8 w-28" />
            <Skeleton className="h-9 w-24" />
          </div>
        </header>
        <Skeleton className="h-10 w-full rounded-lg" />
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Skeleton className="h-8 w-[calc(50%-0.25rem)] sm:w-44" />
          <Skeleton className="h-8 w-56" />
          <Skeleton className="h-8 w-full sm:ml-auto sm:w-72" />
        </div>
      </div>
      <div className="mx-0 mt-4 flex min-h-0 flex-1 flex-col border-y border-border bg-surface sm:mx-6 sm:mb-6 sm:rounded-xl sm:border sm:shadow-xs lg:mx-10">
        <div className="flex items-center gap-4 border-b border-border px-3 pb-2 pt-2.5 sm:px-4">
          <Skeleton className="h-4 w-48" />
          <Skeleton className="h-4 flex-1" />
        </div>
        <LogsSkeleton />
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ Metrics */

export function MetricsSkeleton() {
  return (
    <div role="status" aria-busy="true" aria-live="polite">
      <span className="sr-only">Loading metrics</span>
      {/* Same grid as the stat tiles: 2, 3 or 5 across. */}
      <Card className="grid grid-cols-2 overflow-hidden sm:grid-cols-3 lg:grid-cols-5">
        {Array.from({ length: 5 }, (_, i) => (
          <div key={i} className="p-4 sm:p-5">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="mt-3 h-7 w-20" />
            <Skeleton className="mt-2.5 h-3.5 w-28 max-w-full" />
          </div>
        ))}
      </Card>
      <div className="mt-6 grid gap-5 xl:grid-cols-2">
        {Array.from({ length: 4 }, (_, i) => (
          <Card key={i} className="p-4 sm:p-5">
            <div className="flex items-center gap-3">
              <Skeleton className="size-8 rounded-lg" />
              <Skeleton className="h-5 w-28" />
              <Skeleton className="ml-auto h-5 w-12 rounded-full" />
            </div>
            <Skeleton className="mt-4 h-4 w-56 max-w-full" />
            <Skeleton className="mt-4 h-[190px] w-full" />
            <Skeleton className="mt-3 h-4 w-3/4" />
          </Card>
        ))}
      </div>
    </div>
  );
}

export function MetricsPending() {
  return (
    <Page width="wide">
      <PageHeader title="Metrics" description="How hard your services are working, how fast they answer, and what that costs." actions={<Skeleton className="h-8 w-28" />} />
      <Card className="mb-6 flex flex-wrap items-center gap-3 p-3">
        <Skeleton className="h-8 w-56" />
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-5 w-36 sm:ml-auto" />
      </Card>
      <div className="mb-5 flex items-center gap-2">
        <Skeleton className="h-4 w-14" />
        <Skeleton className="h-7 w-20 rounded-full" />
        <Skeleton className="h-7 w-24 rounded-full" />
        <Skeleton className="h-7 w-24 rounded-full" />
      </div>
      <MetricsSkeleton />
    </Page>
  );
}

/* ------------------------------------------------------------------ Safety Centre */

export function SafetySkeleton() {
  return (
    <Loading label="Loading your safety report">
      {/* Same columns as the grade card and its history. */}
      <div className="grid grid-cols-[minmax(0,1fr)] gap-5 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,1fr)]">
        <Card className="flex flex-col gap-6 p-6 sm:flex-row sm:items-start">
          <Skeleton className="size-28 shrink-0 rounded-2xl" />
          <div className="min-w-0 flex-1">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="mt-3 h-7 w-4/5" />
            <div className="mt-6 flex justify-between">
              <Skeleton className="h-4 w-12" />
              <Skeleton className="h-4 w-16" />
            </div>
            <Skeleton className="mt-2 h-2 w-full rounded-full" />
            <div className="mt-8 flex flex-wrap gap-2">
              <Skeleton className="h-7 w-24 rounded-full" />
              <Skeleton className="h-7 w-28 rounded-full" />
              <Skeleton className="h-7 w-24 rounded-full" />
            </div>
          </div>
        </Card>
        <Card className="p-5">
          <Skeleton className="h-5 w-48" />
          <Skeleton className="mt-2 h-4 w-64 max-w-full" />
          <Skeleton className="mt-5 h-[170px] w-full" />
          <div className="mt-5 space-y-4">
            {Array.from({ length: 3 }, (_, i) => (
              <div key={i} className="flex items-start gap-3">
                <Skeleton className="size-5 rounded" />
                <div className="flex-1 space-y-1.5">
                  <Skeleton className="h-4 w-4/5" />
                  <Skeleton className="h-3 w-28" />
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
      <div className="mt-10">
        <Skeleton className="h-5 w-40" />
        <Skeleton className="mt-2 h-4 w-80 max-w-full" />
        <div className="mt-5 space-y-4">
          <Skeleton className="h-36 w-full rounded-xl" />
          <Skeleton className="h-36 w-full rounded-xl" />
        </div>
      </div>
    </Loading>
  );
}

export function SafetyPending() {
  const slug = useProjectSlug();
  const name = useProject(slug).data?.name ?? slug;
  return (
    <Page>
      <PageHeader
        title="Safety Centre"
        description={`A plain-English security check of ${name}. We re-run it on every deploy and every night.`}
        meta={<Skeleton className="h-6 w-48 rounded-full" />}
        actions={<Skeleton className="h-9 w-32" />}
      />
      <SafetySkeleton />
    </Page>
  );
}
